import React from 'react';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import ThreeContainer from './ThreeContainer';
import DesignDisclaimer from './DesignDisclaimer';
import { Drawing, type CameraOrbit } from '@hi-level/3d-renderer';

const QUOTE_OPTION_QUERY = gql`
  query GetDrawing($uuid: ID!) {
    quoteOptionByUuid(uuid: $uuid) {
      drawing {
        Floor {
          Size {
            XDim
            YDim
            TotalZDim
          }
          Tiers {
            Number
            DeckingDepth
            DeckTopZ
            DeckUndersideZ
            Size {
              XDim
              YDim
              ZDim
            }
            Columns {
              Id
              Position {
                X
                Y
              }
              IsFlagpost
              IsGoalpost
              IsTagged
              IsPassThrough
              ChosenSection
              BaseZ
              TopZ
              Connections {
                Capplates {
                  Id
                  Z
                }
                Beams {
                  Id
                }
              }
            }
            Beams {
              Id
              ConstructionType
              ChosenSection
              Start {
                X
                Y
              }
              End {
                X
                Y
              }
              TopZ
              BottomZ
            }
            Joists {
              Start {
                X
                Y
              }
              End {
                X
                Y
              }
              Connections {
                Beams {
                  Id
                }
                Columns {
                  Id
                }
              }
            }
            Angles {
              CutoutDirection
              Position {
                X
                Y
              }
              Start {
                X
                Y
              }
              End {
                X
                Y
              }
            }
            Cutouts {
              Size {
                XDim
                YDim
              }
              Position {
                X
                Y
              }
            }
          }
          BayDirection
          Ancillaries {
            Id
            Type
            Number
            ModelKey
            Position {
              X
              Y
              Z
            }
            Size {
              Width
              Depth
              Height
            }
            Rotation
            TierNumber
            Edge
            SnappedEdge
            IsSideMounted
            TreadWidth
            Posts {
              X
              Y
              Z
              Type
            }
            Rails {
              Type
              Start {
                X
                Y
                Z
              }
              End {
                X
                Y
                Z
              }
              Z
            }
            Elbows {
              Position {
                X
                Y
              }
              Z
              Rotation
            }
            StaircaseComponents {
              Type
              Position {
                X
                Y
                Z
              }
              Angle
              Length
              Rotation
              TreadType
              Width
              Depth
            }
          }
        }
      }
    }
  }
`;

interface QueryData {
  quoteOptionByUuid: {
    drawing: Drawing;
  };
}

type Props = {
  uuid: string;
  /** Where to open the camera, when the quotation named an angle. */
  openAngle?: CameraOrbit | null;
};

const DrawingContainer: React.FC<Props> = ({ uuid, openAngle }) => {
  const { loading, error, data } = useQuery<QueryData>(QUOTE_OPTION_QUERY, {
    variables: { uuid },
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="size-10 rounded-full border-[3px] border-gray-200 border-t-brand-red animate-spin" />
          <p className="text-sm text-gray-500 font-medium">Loading 3D model&hellip;</p>
        </div>
      </div>
    );
  }

  if (error || !data?.quoteOptionByUuid) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="flex flex-col items-center gap-3 max-w-sm text-center px-4">
          <div className="size-12 rounded-full bg-red-50 flex items-center justify-center">
            <svg
              className="size-6 text-brand-red"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-brand-dark">Unable to load drawing</h2>
          <p className="text-sm text-gray-500">
            We couldn&rsquo;t find a drawing for this link. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-full">
      <DesignDisclaimer />
      <ThreeContainer
        drawing={data.quoteOptionByUuid.drawing as Drawing}
        uuid={uuid}
        openAngle={openAngle}
      />
    </div>
  );
};

export default DrawingContainer;
